import DownloadIcon from 'assets/icons/controls/DownloadIcon';
import Button from 'components/buttons/Button';
import { ActivityType } from 'models/Activity';
import { LearnerModule } from 'models/LearnerCourse';
import strings from 'strings/strings.json';

type DownloadICSButtonProps = {
    courseTitle: string;
    modules: LearnerModule[];
};

export default function DownloadICSButton({ courseTitle, modules }: DownloadICSButtonProps) {
    return (
        <Button
            onClick={() => downloadTrainingICS(courseTitle, modules)}
            title={strings.downloadICSButton.title}
            aria={strings.downloadICSButton.aria}
            icon={<DownloadIcon />}
        />
    );
}

type ICSEvent = {
    DTSTART: string;
    DTEND: string;
    SUMMARY: string;
    DESCRIPTION: string;
    LOCATION: string;
};

function formatICSDate(date: Date): string {
    return date
        .toISOString()
        .replace(/[-:]/g, '') // remove dashes and colons
        .replace(/\.\d{3}Z$/, 'Z'); // remove milliseconds
}

function downloadTrainingICS(courseTitle: string, modules: LearnerModule[]) {
    const escapeICS = (text = '') =>
        text.replace(/\\/g, '\\\\').replace(/\n/g, '\\n').replace(/,/g, '\\,').replace(/;/g, '\\;');

    const events: ICSEvent[] = modules
        .flatMap((x) => x.activities)
        .filter((a) => a.type === ActivityType.LIVE_TRAINING && a.startDate !== undefined)
        .map((a) => {
            const startDate = new Date(a.startDate!);
            const durationMs = (a.durationInMins ?? 0) * 60 * 1000; // convert minutes to ms
            const endDate = new Date(startDate.getTime() + durationMs);

            return {
                DTSTART: formatICSDate(startDate),
                DTEND: formatICSDate(endDate),
                SUMMARY: a.title,
                DESCRIPTION: a.joinLink ?? '',
                LOCATION: a.location ?? '',
            };
        });

    let ics = 'BEGIN:VCALENDAR\r\n';
    ics += 'VERSION:2.0\r\n';
    ics += 'CALSCALE:GREGORIAN\r\n';

    for (const event of events) {
        ics += 'BEGIN:VEVENT\r\n';
        ics += `UID:${crypto.randomUUID()}\r\n`;
        ics += `DTSTAMP:${formatICSDate(new Date())}\r\n`;
        ics += `DTSTART:${event.DTSTART}\r\n`;
        ics += `DTEND:${event.DTEND}\r\n`;
        ics += `SUMMARY:${escapeICS(event.SUMMARY)}\r\n`;
        ics += `DESCRIPTION:${escapeICS(event.DESCRIPTION)}\r\n`;
        ics += `LOCATION:${escapeICS(event.LOCATION)}\r\n`;
        ics += 'END:VEVENT\r\n';
    }

    ics += 'END:VCALENDAR';

    const blob = new Blob([ics], { type: 'text/calendar;charset=utf-8' });
    const url = URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = url;
    link.download = `${courseTitle}.ics`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    URL.revokeObjectURL(url);
}
